import React, { useEffect, useState } from 'react';
import { Save, Plus, X, Clock, ToggleLeft, ToggleRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from './api';

const DAYS = [
  { key: 'lundi',    label: 'Lundi' },
  { key: 'mardi',    label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi',    label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi',   label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' },
];

const DURATIONS = [
  { value: 15,  label: '15 min' },
  { value: 20,  label: '20 min' },
  { value: 30,  label: '30 min' },
  { value: 45,  label: '45 min' },
  { value: 60,  label: '1 h' },
  { value: 90,  label: '1 h 30' },
  { value: 120, label: '2 h' },
];

const DEFAULT_CONFIG = {
  active: true,
  slot_duration_minutes: 30,
  days: {
    lundi:    { active: true,  ranges: [{ start: '09:00', end: '12:00' }, { start: '14:00', end: '17:00' }] },
    mardi:    { active: true,  ranges: [{ start: '09:00', end: '12:00' }, { start: '14:00', end: '17:00' }] },
    mercredi: { active: true,  ranges: [{ start: '09:00', end: '12:00' }, { start: '14:00', end: '17:00' }] },
    jeudi:    { active: true,  ranges: [{ start: '09:00', end: '12:00' }, { start: '14:00', end: '17:00' }] },
    vendredi: { active: true,  ranges: [{ start: '09:00', end: '13:00' }] },
    samedi:   { active: false, ranges: [] },
    dimanche: { active: false, ranges: [] },
  },
};

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

export default function AvailabilityEditor() {
  const [config, setConfig] = useState(deepClone(DEFAULT_CONFIG));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    api('/availability')
      .then((d) => {
        if (d.configured && d.config) {
          setConfig({
            active: d.config.active ?? true,
            slot_duration_minutes: d.config.slot_duration_minutes ?? 30,
            days: { ...deepClone(DEFAULT_CONFIG.days), ...d.config.days },
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const flash = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const setDay = (dayKey, patch) =>
    setConfig((c) => ({ ...c, days: { ...c.days, [dayKey]: { ...c.days[dayKey], ...patch } } }));

  const addRange = (dayKey) =>
    setDay(dayKey, { ranges: [...(config.days[dayKey]?.ranges || []), { start: '09:00', end: '17:00' }] });

  const removeRange = (dayKey, idx) =>
    setDay(dayKey, { ranges: config.days[dayKey].ranges.filter((_, i) => i !== idx) });

  const setRange = (dayKey, idx, field, val) => {
    const ranges = config.days[dayKey].ranges.map((r, i) => i === idx ? { ...r, [field]: val } : r);
    setDay(dayKey, { ranges });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api('/availability', { method: 'PUT', body: config });
      flash('ok', 'Disponibilités enregistrées.');
    } catch (e) {
      flash('err', e.message || 'Erreur lors de l\'enregistrement.');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="py-6 flex justify-center text-slate-400 text-sm">Chargement…</div>
  );

  return (
    <div data-testid="availability-editor" className="space-y-4">

      {/* En-tête : actif / durée */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setConfig((c) => ({ ...c, active: !c.active }))}
            data-testid="avail-toggle-active"
            className={`transition-colors ${config.active ? 'text-emerald-500' : 'text-slate-300'}`}
          >
            {config.active ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
          </button>
          <div>
            <p className="text-sm font-semibold text-slate-800">Prise de RDV en ligne</p>
            <p className="text-xs text-slate-400">{config.active ? 'Activée — visible sur le site' : 'Désactivée — formulaire simple uniquement'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Clock size={14} className="text-slate-400" />
          <span className="text-xs font-semibold text-slate-500">Durée d'un créneau</span>
          <select
            value={config.slot_duration_minutes}
            onChange={(e) => setConfig((c) => ({ ...c, slot_duration_minutes: Number(e.target.value) }))}
            data-testid="avail-duration-select"
            className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white outline-none focus:border-slate-400"
          >
            {DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      </div>

      {/* Grille des jours */}
      <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-50">
        {DAYS.map(({ key, label }) => {
          const day = config.days[key] || { active: false, ranges: [] };
          return (
            <div key={key} className={`p-4 transition-colors ${!day.active ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-[100px]">
                  <button
                    onClick={() => setDay(key, { active: !day.active })}
                    data-testid={`avail-day-toggle-${key}`}
                    className={`transition-colors ${day.active ? 'text-emerald-500' : 'text-slate-300'}`}
                  >
                    {day.active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                  </button>
                  <span className="text-sm font-semibold text-slate-700 w-20">{label}</span>
                </div>

                {day.active ? (
                  <div className="flex-1 space-y-2">
                    {day.ranges.map((range, idx) => (
                      <div key={idx} className="flex items-center gap-2 flex-wrap">
                        <input
                          type="time" value={range.start}
                          onChange={(e) => setRange(key, idx, 'start', e.target.value)}
                          data-testid={`avail-range-start-${key}-${idx}`}
                          className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 outline-none focus:border-slate-400"
                        />
                        <span className="text-xs text-slate-400 font-medium">→</span>
                        <input
                          type="time" value={range.end}
                          onChange={(e) => setRange(key, idx, 'end', e.target.value)}
                          data-testid={`avail-range-end-${key}-${idx}`}
                          className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 outline-none focus:border-slate-400"
                        />
                        <button
                          onClick={() => removeRange(key, idx)}
                          className="p-1 rounded text-slate-300 hover:text-red-400 transition-colors"
                          title="Supprimer cette plage"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addRange(key)}
                      data-testid={`avail-add-range-${key}`}
                      className="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 font-semibold"
                    >
                      <Plus size={12} /> Ajouter une plage
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 italic">Fermé</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Barre de sauvegarde */}
      <div className="flex items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-slate-200">
        <div className="text-xs min-h-[16px]">
          {feedback && (
            feedback.type === 'ok'
              ? <span className="flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle2 size={13} /> {feedback.msg}</span>
              : <span className="flex items-center gap-1 text-red-600 font-semibold"><AlertCircle size={13} /> {feedback.msg}</span>
          )}
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="avail-save-btn"
          className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
        >
          <Save size={14} />
          {saving ? 'Enregistrement…' : 'Enregistrer les disponibilités'}
        </button>
      </div>
    </div>
  );
}
